import { SchmancyElement } from '@schmancy/mixins/index'
import { css, html } from 'lit'
import { customElement, property } from 'lit/decorators.js'

@customElement('schmancy-divider')
export default class SchmancyDivider extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: block;
			}

			@keyframes grow-horizontal {
				from {
					transform: scaleX(0);
				}
				to {
					transform: scaleX(1);
				}
			}

			@keyframes grow-vertical {
				from {
					transform: scaleY(0);
				}
				to {
					transform: scaleY(1);
				}
			}

			.grow-start:not(.h-full) {
				animation: grow-horizontal 400ms ease-out;
				transform-origin: left;
			}

			.grow-end:not(.h-full) {
				animation: grow-horizontal 400ms ease-out;
				transform-origin: right;
			}

			.grow-both:not(.h-full) {
				animation: grow-horizontal 400ms ease-out;
				transform-origin: center;
			}

			.grow-start.h-full {
				animation: grow-vertical 400ms ease-out;
				transform-origin: top;
			}

			.grow-end.h-full {
				animation: grow-vertical 400ms ease-out;
				transform-origin: bottom;
			}

			.grow-both.h-full {
				animation: grow-vertical 400ms ease-out;
				transform-origin: center;
			}

			/*
			 * A rule appearing is a decorative entrance, not information, so a reader
			 * who asked the OS for less motion gets the rule already drawn. Measured:
			 * under prefers-reduced-motion:reduce every grow-* keyframe above still
			 * ran its full 400ms while the host's own transitions correctly stopped,
			 * so the gate existed everywhere except here. animation:none rather
			 * than a zero duration, so no frame of the scaled state is ever painted.
			 */
			@media (prefers-reduced-motion: reduce) {
				.grow-start,
				.grow-end,
				.grow-both,
				.grow-start.h-full,
				.grow-end.h-full,
				.grow-both.h-full {
					animation: none;
				}
			}
		`,
	]

	@property({ type: String }) outline: 'default' | 'variant' = 'variant'
	@property({ type: Boolean }) vertical = false
	@property({ type: String }) grow: 'start' | 'end' | 'both' = 'start'

	/** @deprecated Use `vertical` instead; removed in the next major version. */
	@property({ reflect: true, type: String })
	set orientation(value: 'horizontal' | 'vertical') {
		this.vertical = value === 'vertical'
	}
	get orientation(): 'horizontal' | 'vertical' {
		return this.vertical ? 'vertical' : 'horizontal'
	}

	protected render() {
		return html`<div
			class=${this.classMap({
				'w-full h-px': !this.vertical,
				'h-full w-px': this.vertical,
				'border-outlineVariant': this.outline === 'variant',
				'border-outline': this.outline === 'default',
				'border-t': !this.vertical,
				'border-l': this.vertical,
				[`grow-${this.grow}`]: true,
			})}
		></div>`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-divider': SchmancyDivider
	}
}
